import type { JSX } from "react";
import type { ClassNameProp } from "@charterindex/armoury-www";
import type { ButtonProps } from "@mui/material";
import type { PanelAction } from "./panel";
import { Button, Tooltip, css, styled } from "@mui/material";
import { useTranslation } from "react-i18next";


const PanelActionsRoot = styled("footer")(({ theme }) => css`
	display: flex;
	flex-direction: row-reverse;
	justify-content: space-between;

	box-shadow: ${theme.shadows[1]} inset;
	
	> .MuiButton-root {
		height: ${theme.layout.targetSize};
		padding: ${theme.spacing(0, 1)};
	}
`);

const getButtonColor = (color: PanelAction["color"], defaultColor: ButtonProps["color"]): ButtonProps["color"] => {
	switch(color) {
		case "primary":
			return "primary";
		case "danger":
			return "error";
		case "secondary":
		default:
			return defaultColor;
	}
};

const matchesCondition = (condition: PanelAction["condition"]): boolean => {
	if(condition === undefined) {
		return true;
	}

	if(typeof(condition) === "function") {
		return condition();
	}

	return condition;
};

type PanelActionButtonProps = {
	action?: Readonly<PanelAction> | undefined | null;
	disabled?: boolean;
	defaultColor: ButtonProps["color"];
};
const PanelActionButton = ({ action, disabled, defaultColor }: PanelActionButtonProps): JSX.Element => {
	const { t } = useTranslation();

	if(!action || !matchesCondition(action.condition)) {
		return <span/>;
	}

	const label = action.label || t(action.labelKey!, action.labelValues!);

	const button = (
		<Button
			variant="text"
			startIcon={ action.icon && <action.icon/> }
			color={ getButtonColor(action.color, defaultColor) }
			type={ action.submitFormId ? "submit" : "button" }
			onClick={ action.onClick ?? undefined }
			form={ action.submitFormId ?? undefined }
			disabled={ disabled || action.disabled }
		>
			{ label }
		</Button>
	);

	if(action.tooltip || action.tooltipKey) {
		return (
			<Tooltip title={ action.tooltip ?? t<string>(action.tooltipKey as string, action.tooltipValues ?? {}) }>
				<span>
					{ button }
				</span>
			</Tooltip>
		);
	}

	return button;
};

export type PanelActionsProps = ClassNameProp & {
	primaryAction?: PanelAction | undefined | null;
	secondaryAction?: PanelAction | undefined | null;
	disabled?: boolean;
};
export const PanelActions = ({ className, primaryAction, secondaryAction, disabled }: PanelActionsProps): JSX.Element | null => {
	if(!primaryAction && !secondaryAction) {
		return null;
	}

	return (
		<PanelActionsRoot className={ className }>
			<PanelActionButton
				action={ primaryAction }
				defaultColor="primary"
				disabled={ disabled }
			/>
			<PanelActionButton
				action={ secondaryAction }
				defaultColor="inherit"
				disabled={ disabled }
			/>
		</PanelActionsRoot>
	);
};
